import Button from '@shared/components/Button/Button';
import cx from 'classnames';
import { PropsWithChildren, isValidElement } from 'react';

import { ReactComponent as X } from 'assets/icons/X.svg';
import { IconType } from 'utils/types';

const iconVariants = {
  info: { icon: 'stroke-black', background: 'bg-gray-20' },
  success: { icon: 'stroke-white', background: 'bg-complementary-50' },
  warning: { icon: 'stroke-white', background: 'bg-tertiary-50' },
};

export type NotificationToastVariant = keyof typeof iconVariants;

export interface NotificationToastProps {
  variant?: NotificationToastVariant;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  primaryActionText?: string;
  secondaryActionText?: string;
  icon?: IconType | JSX.Element;
  description?: string;
  className?: string;
  closeButtonTestId?: string;
  onClose: () => void;
}

const NotificationToast = ({
  variant = 'success',
  onPrimaryAction,
  onSecondaryAction,
  primaryActionText,
  secondaryActionText,
  icon,
  description,
  className,
  closeButtonTestId,
  onClose,
  children,
}: PropsWithChildren<NotificationToastProps>) => {
  const hasAnyAction = !!primaryActionText || !!secondaryActionText;

  const renderIcon = () => {
    if (isValidElement(icon)) return icon;
    if (typeof icon === 'function') {
      const Icon = icon;
      return (
        <div className={cx('rounded-xl h-fit w-fit p-1.5', iconVariants[variant].background)}>
          <Icon className={cx('shrink-0 stroke-1.5 h-5 w-5', iconVariants[variant].icon)} />
        </div>
      );
    }

    return null;
  };

  const handleSecondaryAction = () => {
    onSecondaryAction?.();
    onClose();
  };

  const handlePrimaryAction = () => {
    onPrimaryAction?.();
    onClose();
  };

  return (
    <div
      className={cx(
        'relative flex h-fit w-full max-w-100 rounded-2xl bg-white border border-gray-30 p-4 gap-3',
        className
      )}
    >
      {renderIcon()}
      <div className="flex flex-col flex-1 gap-2 overflow-hidden">
        <div className="flex gap-2">
          <div className="flex flex-col gap-1 text-size-sm overflow-hidden">
            <span className="text-black">{children}</span>
            {description && <span className="text-gray-80 truncate">{description}</span>}
          </div>
          <X
            className="shrink-0 cursor-pointer stroke-1.5 stroke-current"
            onClick={onClose}
            data-testid={closeButtonTestId}
          />
        </div>
        {hasAnyAction && (
          <div className="flex justify-end flex-wrap gap-4">
            {!!secondaryActionText && (
              <Button variant="secondaryTertiary" size="sm" onClick={handleSecondaryAction}>
                {secondaryActionText}
              </Button>
            )}
            {!!primaryActionText && (
              <Button variant="primary" size="sm" onClick={handlePrimaryAction}>
                {primaryActionText}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationToast;
